import {Grid, TextField, useTheme} from "@mui/material";
import {observer} from 'mobx-react';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import * as React from "react";
import {useApplication} from "../../ApplicationProvider";
import {useNavigatorFeature} from "../../useFeature";
import FormSection from "../../forms/FormSection";
import LayoutContentWrapper from "../../components/layout/LayoutContentWrapper";
import LayoutContentToolbar from "../../components/layout/LayoutContentToolbar";
import {CUSTOMERS_PATH} from "../../routes";
import {styles} from "../orders/create_order/CreateOrderStyles";
import CustomerFeature from "./CustomerFeature";
import FilledButton from "../../components/buttons/FilledButton";
import TabsPanel from "../../components/tabsPanel/TabsPanel";
import Table from "../../components/table/Table";
import PersonIcon from "@mui/icons-material/Person";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import FormEmailField from "../../components/fields/FormEmailField";
import PlaceIcon from "@mui/icons-material/Place";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DescriptionIcon from "@mui/icons-material/Description";
import TaxProfilePanel from "./TaxProfilePanel";
import DeliverySitesPanel from "./DeliverySitesPanel";
import {renderAuthorizationStateIcon} from "../../components/state/AuthorizationStateRenderer";
import SyncIcon from "@mui/icons-material/Sync";

function CustomerScreen() {
    const application = useApplication();
    const theme = useTheme();
    const navigator = useNavigate();
    const location = useLocation();
    const {id} = useParams();
    const feature = useNavigatorFeature(
        () => new CustomerFeature(application, navigator, id, location.state?.customer),
        navigator,
        [id]
    )
    const style = styles(theme);

    const isFeatureReady = (feature) => {
        return feature && feature.loaded
    }

    const renderCustomerInformationPanel = () => {
        return (
            <FormSection>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Razón social"
                            value={feature.businessName()}
                            fullWidth
                            InputProps={{readOnly: true}}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Número de cliente"
                            value={feature.customerCode()}
                            fullWidth
                            InputProps={{readOnly: true}}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="CUIT"
                            value={feature.taxpayerIdentificationNumber()}
                            fullWidth
                            InputProps={{readOnly: true}}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Nombre de fantasía"
                            value={feature.fantasyName()}
                            fullWidth
                            InputProps={{readOnly: true}}
                        />
                    </Grid>
                    {feature.franchiseBrandName() && (
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Franquicia"
                                value={feature.franchiseBrandName()}
                                fullWidth
                                InputProps={{readOnly: true}}
                            />
                        </Grid>
                    )}
                </Grid>
            </FormSection>
        );
    };

    const renderContactPanel = () => {
        return (
            <FormSection>
                <Grid container spacing={2}>
                    <FormEmailField
                        label="Mail de cobranzas"
                        placeholder="Mail de cobranzas"
                        field={feature.collectionsEmailField()}
                        onBlur={(event) => feature.editCollectionsEmail(event.target.value)}
                    />
                </Grid>
            </FormSection>
        );
    };

    const renderDeliverySiteOnboardingsPanel = () => {
        return (
            <>
                {(application.hasOnboardingERPImporterPermission() || application.canManageDeliverySiteOnboardings()) && (
                    <div style={{
                        ...style.buttonsContainer,
                        justifyContent: 'flex-end',
                        paddingTop: '0.75rem',
                        paddingRight: '0.75rem',
                        marginBottom: '1rem',
                        gap: '1rem'
                    }}>
                        {application.hasOnboardingERPImporterPermission() && (
                            <FilledButton
                                onClick={() => feature.synchronizeERP()}
                                loading={feature.buttonLoading}
                                variant="contained"
                                disabled={feature.buttonLoading}>
                                {feature.buttonLoading ? "Sincronizando..." : "Sincronización con ERP"}
                                <SyncIcon fontSize="small" style={{marginLeft: '8px'}}/>
                            </FilledButton>
                        )}
                        {application.canManageDeliverySiteOnboardings() && (
                            <FilledButton onClick={() => feature.navigateToCreateDeliverySiteOnboarding()}>
                                Iniciar Alta de Punto de Entrega
                            </FilledButton>
                        )}
                    </div>
                )}
                <Table
                    columns={feature.deliverySiteOnboardingsColumns()}
                    rows={feature.presentedDeliverySiteOnboardings()}
                    navigateToRowObjectScreen={(params) => feature.navigateToDeliverySiteOnboarding(params.row)}
                    columnsRenderer={{
                        taxAuthorization: renderAuthorizationStateIcon,
                        logisticsAuthorization: renderAuthorizationStateIcon,
                    }}
                />
            </>
        );
    };

    const tabs = [
        {
            label: () => "Cliente",
            title: () => "",
            icon: () => <PersonIcon/>,
            style: () => null,
            panel: () => renderCustomerInformationPanel(),
        },
        {
            label: () => "Contacto",
            title: () => "",
            icon: () => <ContactMailIcon/>,
            style: () => null,
            panel: () => renderContactPanel(),
        },
        {
            label: () => "Perfil Fiscal",
            title: () => "",
            icon: () => <DescriptionIcon/>,
            style: () => null,
            panel: () => <TaxProfilePanel feature={feature}/>,
        },
        {
            label: () => "Puntos de Entrega",
            title: () => "",
            icon: () => <LocalShippingIcon/>,
            style: () => null,
            panel: () => <DeliverySitesPanel application={application} navigator={navigator} businessName={id}/>,
        },
        {
            label: () => "Altas de punto de entrega",
            title: () => "",
            icon: () => <PlaceIcon/>,
            style: () => null,
            panel: () => renderDeliverySiteOnboardingsPanel(),
        },
    ];

    const renderContent = () => {
        return (
            <div>
                <LayoutContentToolbar
                    title={feature.fantasyName() || "Cliente"}
                    breadcrumbs={[
                        {label: 'Clientes', path: CUSTOMERS_PATH},
                        {label: feature.fantasyName() || "Cliente"},
                    ]}
                />
                <FormSection>
                    <TabsPanel tabs={tabs} variant="scrollable"/>
                </FormSection>
            </div>
        );
    };

    return <LayoutContentWrapper isLoading={!isFeatureReady(feature)} renderContent={() => renderContent()}/>;
}
export default observer(CustomerScreen);
